import { type ReactNode, type RefObject, useEffect, useRef } from "react";
import { saidStep } from "./said-in-view";

/**
 * Pinned to the bottom of the screen while there is something to decide or
 * save, so it never waits on scrolling back; beside the sidebar on wider
 * screens. A page that shows it leaves room under its content (`pb-36`). From
 * 1024 px its content stands under the form, beside the card's photos
 * (`PHOTOS_BESIDE`): the page's padding, then the photos' column and its gap.
 */
export function BottomBar({ children }: { children: ReactNode }) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-app-elevated/95 backdrop-blur sm:left-52 pr-[env(safe-area-inset-right)]">
      <div className="mx-auto max-w-[700px] px-5 pt-2.5 pb-[max(0.5rem,env(safe-area-inset-bottom))] lg:max-w-[1064px] lg:pl-[calc(1.25rem+20.75rem+2rem)]">
        {children}
      </div>
    </div>
  );
}

/**
 * Brings what a screen says at its top into view when it is new there, by
 * `saidStep`'s rule. The element keeps a top scroll margin (`scroll-mt-12`),
 * clear of the on-air bar fixed over the page.
 */
export function useSaidInView(
  ref: RefObject<HTMLElement | null>,
  said: string | null,
  atTop: boolean,
  busy: boolean,
): void {
  const seen = useRef<string | null>(null);
  useEffect(() => {
    const step = saidStep(seen.current, said, atTop, busy);
    if (step.scroll) ref.current?.scrollIntoView({ block: "nearest" });
    seen.current = step.seen;
  }, [ref, said, atTop, busy]);
}
